import { useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { GetJobResponse } from "@/types/responses";
import { JobDetails, JobMeta } from "@/types/utils";

const POLL_MS = 2000;

function usePollJob<Result, Metadata extends JobMeta = JobMeta>(
  jobId: string | null
) {
  const [jobData, setJobData] = useState<JobDetails<Result, Metadata> | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(() => jobId != null);

  const client = useAxiosClient("/jobs");

  // Reset state during render (not in an effect) when a new job starts. Mirrors
  // the original effect, which only reset when jobId was truthy (it returned
  // early otherwise), so switching to a null jobId leaves prior state intact.
  const [prevJobId, setPrevJobId] = useState(jobId);
  if (prevJobId !== jobId) {
    setPrevJobId(jobId);
    if (jobId) {
      setJobData(null);
      setError(null);
      setPolling(true);
    }
  }

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const response = await client.get<GetJobResponse<Result, Metadata>>(
          `/${jobId}`
        );
        if (cancelled) return;
        setJobData(response.data.job);
        setError(null);
        const status = response.data.job.status;
        if (status === "finished" || status === "failed") {
          setPolling(false);
          return;
        }
        timeoutId = setTimeout(() => {
          void poll();
        }, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setPolling(false);
        setError(e instanceof Error ? e.message : String(e));
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [client, jobId]);

  return {
    error,
    jobData,
    polling,
  };
}

export { usePollJob };
